/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useContext } from "react";
import CreateIRModal from "../../components/Modals/CreateIRModal";
import { Loading } from "./AllReports";
import { NavLink } from "react-router-dom";
import { LayoutContext } from "../../components/layout";
import { GET_PATIENTS_API_V2_ENABLED } from "../../constants/AppConfigConstants";

export const calculateAge = (birthdayTs) => {
  if (!birthdayTs) return "-";
  const birthday = new Date(birthdayTs);
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs); // miliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

const PatientsTable = ({ data, initialLoad, sortingFunction }) => {
  const { appConfigStructure } = useContext(LayoutContext);
  const [selected, setSelected] = useState(null);
  const [lastNameOrder, setLastNameOrder] = useState(null);
  const [firstNameOrder, setFirstNameOrder] = useState(null);
  const [mnrOrder, setMnrOrder] = useState(null);

  const handleSort = (field) => {
    switch (field) {
      case "lastName":
        setFirstNameOrder(null);
        setMnrOrder(null);
        if (lastNameOrder === null) {
          setLastNameOrder(true);
          sortingFunction(["family", "id"]);
        } else if (lastNameOrder) {
          setLastNameOrder(false);
          sortingFunction(["-family", "id"]);
        } else {
          setLastNameOrder(null);
          sortingFunction(null);
        }
        break;
      case "firstName":
        setLastNameOrder(null);
        setMnrOrder(null);
        if (firstNameOrder === null) {
          setFirstNameOrder(true);
          sortingFunction(["name", "id"]);
        } else if (firstNameOrder) {
          setFirstNameOrder(false);
          sortingFunction(["-name", "id"]);
        } else {
          setFirstNameOrder(null);
          sortingFunction(null);
        }
        break;
      case "mrn":
        setLastNameOrder(null);
        setFirstNameOrder(null);
        if (mnrOrder === null) {
          setMnrOrder(true);
          if (appConfigStructure[GET_PATIENTS_API_V2_ENABLED]) {
            sortingFunction(["patientLocationMrn", "id"]);
          } else {
            sortingFunction(["patientLocation.mrn", "id"]);
          }
        } else if (mnrOrder) {
          setMnrOrder(false);
          if (appConfigStructure[GET_PATIENTS_API_V2_ENABLED]) {
            sortingFunction(["-patientLocationMrn", "id"]);
          } else {
            sortingFunction(["-patientLocation.mrn", "id"]);
          }
        } else {
          setMnrOrder(null);
          sortingFunction(null);
        }
        break;
    }
  };

  return (
    <>
      <div className="contentTbl">
        <table id="patientsTableId" className="overview">
          <thead>
            <tr>
              <th style={{ width: "4%" }}>Date Added</th>
              <th
                style={{ width: "15%" }}
                className={
                  lastNameOrder == null ? "sortDisable" : lastNameOrder ? "headerSortUp" : "headerSortDown"
                }
              >
                <a href="#" onClick={() => handleSort("lastName")}>
                  Last Name
                </a>
              </th>
              <th
                style={{ width: "15%" }}
                className={
                  firstNameOrder == null ? "sortDisable" : firstNameOrder ? "headerSortUp" : "headerSortDown"
                }
              >
                <a href="#" onClick={() => handleSort("firstName")}>
                  First Name
                </a>
              </th>
              <th style={{ width: "4%" }}>Age</th>
              <th style={{ width: "5%" }}>Gender</th>
              <th
                style={{ width: "5%" }}
                className={mnrOrder == null ? "sortDisable" : mnrOrder ? "headerSortUp" : "headerSortDown"}
              >
                <a href="#" onClick={() => handleSort("mrn")}>
                  MRN
                </a>
              </th>
              <th style={{ width: "8%" }}>Email</th>
              <th style={{ width: "17%" }}>Following</th>
              <th style={{ width: "8%" }}>Home Phone</th>
              <th style={{ width: "8%" }}>Mobile Phone</th>
              <th style={{ width: "10%" }}>Locations</th>
              <th style={{ width: "1%" }}></th>
            </tr>
          </thead>

          {initialLoad ? (
            <Loading />
          ) : data.length > 0 ? (
            <tbody>
              {data.map((item, index) => (
                <tr key={item?.id}>
                  <td style={{ width: "4%" }}>{new Date(item?.createdAt).toLocaleDateString()}</td>
                  <td style={{ width: "15%" }}>{item?.family}</td>
                  <td style={{ width: "15%" }}>{item?.name}</td>
                  <td style={{ width: "4%" }}>{calculateAge(item?.dob)}</td>
                  <td style={{ width: "5%" }}>{item?.gender}</td>
                  <td style={{ width: "5%" }}>
                    {appConfigStructure[GET_PATIENTS_API_V2_ENABLED]
                      ? item?.patientLocationMrn
                      : item?.patientLocation?.map((item) => item?.mrn).join(",")}
                  </td>
                  <td style={{ width: "8%" }}>{item?.emailAddress}</td>
                  <td style={{ width: "17%" }}>
                    {appConfigStructure[GET_PATIENTS_API_V2_ENABLED] && item.followingPhysicianFamily && item.followingPhysicianName
                      ? item.followingPhysicianFamily + " " + item.followingPhysicianName
                      : item.followingPhysician
                      ? item.followingPhysician.family + ", " + item.followingPhysician.name
                      : "-"}
                  </td>
                  <td style={{ width: "8%" }}>{item?.homePhone}</td>
                  <td style={{ width: "8%" }}>{item?.mobilePhone}</td>
                  <td style={{ width: "10%" }}>
                    {appConfigStructure[GET_PATIENTS_API_V2_ENABLED]
                      ? Array.isArray(item?.patientLocationName)
                        ? ""
                        : item?.patientLocationName
                      : item?.patientLocation?.map((item) => item?.location?.name).join(",")}
                  </td>
                  <td style={{ width: "1%" }}>
                    <div id={`view-btn-${index}`} className="show-options">
                      <NavLink
                        exact
                        to={`/patients3/${item?.id}`}
                        className="btn btnTeal"
                        onClick={() => {
                          localStorage.setItem("master-location", "/patients3?return=true");
                        }}
                        id="viewEditBtnId"
                      >
                        View/Edit
                      </NavLink>
                      <a
                        id="createIRBtnId"
                        href="#popupIR"
                        className="btn btnTeal"
                        onClick={() => {
                          if (appConfigStructure[GET_PATIENTS_API_V2_ENABLED]) {
                            item["patientLocation"] = [
                              {
                                id: item?.patientLocationId,
                                patient: {
                                  id: item.id,
                                  name: item.name,
                                  family: item.family,
                                  dob: item.dob,
                                  gender: item.gender,
                                },
                                location: {
                                  id: item?.locationId,
                                  name: item?.patientLocationName,
                                },
                                mrn: item?.patientLocationMrn,
                              },
                            ];
                          }

                          setSelected(item);
                        }}
                      >
                        Create IR
                      </a>
                      {/* <button className="btn btnUrgent">Esign</button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : null}
        </table>
        {data.length === 0 && !initialLoad ? (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 300,
              fontSize: 18,
            }}
          >
            No patients
          </div>
        ) : null}
      </div>

      <CreateIRModal patient={selected} />
    </>
  );
};

export default PatientsTable;
